import React, {memo, useCallback, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    SimpleGrid,
    useStyleConfig,
    Flex,
    Text,
    Button,
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useBreakpointValue, Switch} from '@chakra-ui/react'
import {useCommerceApi, useAccessToken} from '@salesforce/commerce-sdk-react'
import {useCurrency} from '@salesforce/retail-react-app/app/hooks'
import {useCompare} from '../../../context/compare'
import CompareTile from './compare-tile'
import CompareSelectBar from './compare-select-bar'
import {FormattedMessage} from 'react-intl'
import {remCalc} from '../../../theme/_utils'
import {CloseIcon} from '../../../components/icons/shared'
import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {useGetProductCompareData} from '../../../custom-api/hooks/use-get-product-compare-data'
import {useLocation} from 'react-router-dom'

const getProductKey = (product) => product?.productId || product?.id

const CompareProductTiles = ({
    productsWithCompareData,
    hideIdenticalRows = false,
    onHideIdenticalRowsChange = null,
    categoryId = '',
    locale,
}) => {
    const styles = useStyleConfig('ProductComparePlp')
    const maxCompareItems = useBreakpointValue({base: 2, md: 3}) || 3
    const navigate = useNavigation()
    const location = useLocation()
    const categoriesData = useGetCategoriesData()
    const compareOptions = useGetProductCompareData(categoryId)
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const {currency} = useCurrency()
    const categoryInfo = categoriesData?.[categoryId] ?? null
    const fullCategoryUrl = `${categoryInfo?.url}`
    const localeAliasRegex = locale?.alias?.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const isComparePage = localeAliasRegex
        ? new RegExp(`/${localeAliasRegex}/compare(?:/|$)`).test(location.pathname)
        : false

    const {
        updateItem,
        state: {compareItems},
    } = useCompare()

    const selectionRequests = useRef({})

    const handleSelectProduct = useCallback(
        async (index, selectionId) => {
            if (typeof index !== 'number' || !selectionId) return

            const requestId = (selectionRequests.current[index] || 0) + 1
            selectionRequests.current[index] = requestId

            try {
                const token = await getTokenWhenReady()
                const fetchCompareProduct = (id) =>
                    api.shopperProducts.getProduct({
                        parameters: {
                            id,
                            locale: locale?.id,
                            currency,
                            c_includeTechSpecs: true,
                            expand: ['availability', 'images', 'prices', 'promotions'],
                        },
                        headers: {Authorization: `Bearer ${token}`},
                    })
                let nextProduct = await fetchCompareProduct(selectionId)
                if (selectionRequests.current[index] !== requestId) return

                // Dropdown options identify SKUs; color choices identify variation groups.
                // Resolve both to the same data before exposing the selection to the UI.
                const groupId = nextProduct?.c_subModelIdentifier
                const enabledGroup = nextProduct?.c_enabledVariationGroups?.some(
                    (group) => (group.productId || group.id) === groupId,
                )
                if (groupId && groupId !== nextProduct?.id && enabledGroup) {
                    nextProduct = await fetchCompareProduct(groupId)
                }
                if (nextProduct && selectionRequests.current[index] === requestId) {
                    updateItem(index, {
                        ...nextProduct,
                        productId: nextProduct.productId || nextProduct.id,
                        id: nextProduct.id || nextProduct.productId,
                    })
                }
            } catch (e) {
                console.error('Error fetching selected compare product:', e)
            }
        },
        [api, getTokenWhenReady, locale, currency, updateItem],
    )

    const compareSlots = useMemo(
        () => Array.from({length: maxCompareItems}, (_, index) => compareItems[index] || null),
        [compareItems, maxCompareItems],
    )

    const productsWithCompareDataById = useMemo(
        () =>
            new Map(
                (productsWithCompareData || []).map((compareProduct) => [
                    getProductKey(compareProduct),
                    compareProduct,
                ]),
            ),
        [productsWithCompareData],
    )

    const compareColumns = maxCompareItems
    const compareGridMaxWidth = remCalc(compareColumns * 400 + (compareColumns - 1) * 40)

    const handleReturnToPLP = () => {
        navigate(fullCategoryUrl)
    }

    return (
        <>
            <Flex
                width="100%"
                minHeight={remCalc(182)}
                paddingTop={remCalc(64)}
                marginBottom={remCalc(64)}
                alignItems="center"
                justifyContent="flex-start"
                flexDir="column"
                position="relative"
            >
                {isComparePage && (
                    <Button
                        variant="icon-button-ghost"
                        size="small"
                        onClick={handleReturnToPLP}
                        alignSelf="flex-end"
                    >
                        <CloseIcon width="iconsSize.medium" height="iconsSize.medium" />
                    </Button>
                )}
                <Text
                    color="foreground.neutral.default"
                    textStyle="display-small"
                    fontWeight="light"
                    marginBottom={remCalc(8)}
                >
                    <FormattedMessage id="product_tile.compare.label" defaultMessage="Compare" />
                </Text>
                <Text
                    marginBottom={remCalc(16)}
                    color="foreground.neutral.muted"
                    textStyle="body-large"
                    fontWeight="regular"
                >
                    <FormattedMessage
                        defaultMessage="Select products to compare"
                        id="plp.product-compare.select-products"
                    />
                </Text>
                <Flex gap={{base: remCalc(6), md: remCalc(8)}}>
                    <Text>
                        <FormattedMessage
                            defaultMessage="Show only differences"
                            id="plp.product-compare.differences"
                        />
                    </Text>
                    <Switch
                        isChecked={hideIdenticalRows}
                        onChange={(e) => onHideIdenticalRowsChange(e.target.checked)}
                        sx={{
                            '.chakra-switch__track': {
                                bg: 'background.base.deep',
                            },
                            '.chakra-switch__track[data-checked]': {
                                bg: 'background.accent.default',
                            },
                        }}
                    />
                </Flex>
            </Flex>
            <Flex
                width="100%"
                maxW={compareGridMaxWidth}
                mx="auto"
                flexDir="column"
                gap={{base: remCalc(24), md: remCalc(40)}}
            >
                <CompareSelectBar
                    compareColumns={compareColumns}
                    compareGridMaxWidth={compareGridMaxWidth}
                    compareSlots={compareSlots}
                    compareOptions={compareOptions}
                    onSelectProduct={handleSelectProduct}
                />
                <SimpleGrid
                    {...styles.modalProductTilesGrid}
                    columns={compareColumns}
                    templateColumns={`repeat(${compareColumns}, minmax(0, 1fr))`}
                    width="100%"
                    maxW="100%"
                >
                    {compareSlots.map((product, productIndex) =>
                        product ? (
                            <CompareTile
                                key={product?.productId || product?.id || `product-${productIndex}`}
                                product={product}
                                productsWithCompareData={
                                    productsWithCompareDataById.get(getProductKey(product)) || null
                                }
                                productIndex={productIndex}
                                onVariantChange={handleSelectProduct}
                            />
                        ) : null,
                    )}
                </SimpleGrid>
            </Flex>
        </>
    )
}

CompareProductTiles.propTypes = {
    productsWithCompareData: PropTypes.array,
    hideIdenticalRows: PropTypes.bool,
    onHideIdenticalRowsChange: PropTypes.func,
    categoryId: PropTypes.string,
    locale: PropTypes.object,
}

export default memo(CompareProductTiles)
